import { useCallback, useState } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';
import { readEntry, sessionStore, writeEntry } from './viewStore';

const BUCKET = 'onvice.viewMemory.v1';

/**
 * Ansichts-Gedächtnis je Verlaufseintrag: aufgeklappte Gruppen, Nachlade-Stand und
 * ähnlicher Zustand, der nicht in die Adresse gehört. „Zurück" aus dem Dokument stellt ihn
 * wieder her; ein neuer Aufruf der Liste über die Seitenleiste beginnt im Standard.
 *
 * Der Wert muss als JSON darstellbar sein.
 */
export function useViewMemory<T>(name: string, initial: T): [T, (next: T | ((prev: T) => T)) => void] {
  const location = useLocation();
  const navType = useNavigationType();
  const storageKey = `${location.key}|${location.pathname}|${name}`;

  const [state, setState] = useState<{ key: string; value: T }>(() => {
    const store = sessionStore();
    const saved = store ? readEntry<T>(store, BUCKET, storageKey) : undefined;
    return { key: storageKey, value: saved === undefined ? initial : saved };
  });

  if (state.key !== storageKey) {
    const store = sessionStore();
    if (navType === 'REPLACE') {
      // Filterwechsel schreiben die Adresse ersetzend und erzeugen dabei einen neuen
      // Verlaufsschlüssel — der Wert zieht unter den neuen Schlüssel mit um.
      if (store) writeEntry(store, BUCKET, storageKey, state.value);
      setState({ key: storageKey, value: state.value });
    } else {
      // Anderer Verlaufseintrag derselben Seite: dessen eigenen Stand laden.
      const saved = store ? readEntry<T>(store, BUCKET, storageKey) : undefined;
      setState({ key: storageKey, value: saved === undefined ? initial : saved });
    }
  }

  const set = useCallback((next: T | ((prev: T) => T)) => {
    setState((prev) => {
      const value = typeof next === 'function' ? (next as (p: T) => T)(prev.value) : next;
      const store = sessionStore();
      if (store) writeEntry(store, BUCKET, prev.key, value);
      return { key: prev.key, value };
    });
  }, []);

  return [state.value, set];
}
